import { useState } from "react";
import api, { getToken } from "../../services/api";
import {
  LineChart, Line,
  BarChart, Bar,
  ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import ProtectedLink from "../../components/ProtectedLink";

// ── Helpers ──────────────────────────────────────────────────────
function fmt(n)    { return (n||0).toLocaleString("fr-FR",{maximumFractionDigits:0})+" €"; }
function fmtK(n)   { return ((n||0)/1000).toFixed(0)+"k€"; }
function fmtPct(n) { return (n||0).toFixed(1)+"%"; }
function clr(n)    { return (n||0)>=0?"#16a34a":"#dc2626"; }

const DEFAULT_HYP = {
  years:3,
  growth_rates:[5,8,10,12,10],
  payroll_growth:3,
  investments:[0,0,0,0,0],
  is_rate:25,
  amort_rate:15,
  financial_charges:0,
  bfr_days:45,
};

const SIG_ROWS = [
  {label:"Chiffre d'affaires",              key:"ca",           color:"#3b82f6",  bold:true,  section:"top"},
  {label:"Achats & charges externes",       key:"chargesOp",    color:"#dc2626",             section:"top"},
  {label:"Valeur Ajoutée (VA)",             key:"va",           color:"#8b5cf6",  bold:true,  separator:true,section:"top"},
  {label:"Masse salariale (incl. charges)", key:"payroll",      color:"#dc2626",             section:"top"},
  {label:"EBE — Excédent Brut d'Exploit.", key:"ebe",          color:"#1a9e72",  bold:true,  separator:true,section:"top"},
  {label:"Amortissements",                  key:"amort",        color:"#8b5cf6",             section:"pl"},
  {label:"Résultat d'Exploitation (REX)",  key:"rex",          color:"#1a9e72",  bold:true,  separator:true,section:"pl"},
  {label:"Charges financières",             key:"rf",           color:"#dc2626",             section:"pl"},
  {label:"Résultat Courant avant IS",       key:"rai",          color:"#1f2937",             section:"pl"},
  {label:"Impôt sur les Sociétés (IS)",    key:"isAmt",        color:"#6b7280",             section:"pl"},
  {label:"Résultat Net (RN)",              key:"rn",           color:"#16a34a",  bold:true,  separator:true,section:"pl"},
  {label:"— Marges —",                     key:"_sep2",        isSep:true,                   section:"margins"},
  {label:"Marge VA / CA",                  key:"vaMargin",     color:"#8b5cf6",  isPct:true, section:"margins"},
  {label:"Taux EBE / CA",                  key:"ebeMargin",    color:"#1a9e72",  isPct:true, section:"margins"},
  {label:"Marge REX / CA",                 key:"rexMargin",    color:"#1a9e72",  isPct:true, section:"margins"},
  {label:"Marge nette / CA",               key:"rnMargin",     color:"#16a34a",  isPct:true, section:"margins"},
  {label:"— Cash & Financement —",        key:"_sep3",        isSep:true,                   section:"cash"},
  {label:"CAF — Capacité Autofinancement", key:"caf",          color:"#3b82f6",  bold:true,  section:"cash"},
  {label:"Investissements",                 key:"invest",       color:"#ef4444",             section:"cash"},
  {label:"Variation BFR",                  key:"bfrVariation", color:"#f59e0b",             section:"cash"},
  {label:"Free Cash-Flow",                 key:"fcf",          color:"#ec4899",  bold:true,  separator:true,section:"cash"},
];

const card = {
  background:"var(--card-bg,#fff)",
  border:"1px solid var(--border-color,#e2e8f0)",
  borderRadius:12,padding:22,marginBottom:20,
};

export default function BusinessPlanTab({scenarioId, planRank}) {
  const [hyp,     setHyp]     = useState(DEFAULT_HYP);
  const [plan,    setPlan]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg,     setMsg]     = useState("");
  const [section, setSection] = useState("all"); // all | top | pl | margins | cash
  const [exporting,setExport] = useState(false);

  const setGrowth = (i,val) => setHyp(h=>{ const g=[...h.growth_rates]; g[i]=parseFloat(val)||0; return{...h,growth_rates:g}; });
  const setInvest = (i,val) => setHyp(h=>{ const g=[...h.investments];   g[i]=parseFloat(val)||0; return{...h,investments:g}; });

  const generate = async()=>{
    if(!scenarioId) return;
    setLoading(true); setMsg("");
    try {
      const r = await api.budget.businessPlan(scenarioId,{
        ...hyp, years:parseInt(hyp.years)||3,
        start_year:new Date().getFullYear(),
      });
      setPlan(r);
    } catch(e){ setMsg("Erreur : "+e.message); }
    finally { setLoading(false); }
  };

  const exportPdf = async()=>{
    setExport(true);
    try {
      const res = await fetch(api.budget.exportPdfUrl(scenarioId),{
        // Jeton de l'application (financeapp_token) — la clé « token » n'existait pas
        headers:{ Authorization:`Bearer ${getToken() || ""}` },
      });
      if(!res.ok) throw new Error("Export indisponible");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href=url; a.download=`BusinessPlan-${scenarioId}.pdf`; a.click();
      setTimeout(()=>URL.revokeObjectURL(url),5000);
    } catch(e){ setMsg("Erreur export : "+e.message); }
    finally { setExport(false); }
  };

  if(!scenarioId) return (
    <div style={{textAlign:"center",padding:60,color:"var(--text-muted)",fontSize:13}}>
      Sélectionnez un scénario de base dans le sélecteur en haut de page.
    </div>
  );

  const inputSt = {width:"100%",padding:"7px 10px",border:"1px solid var(--border-color,#e2e8f0)",
    borderRadius:8,fontSize:12,background:"var(--input-bg,#fff)",boxSizing:"border-box"};

  // Build chart datasets from plan
  const chartRows = plan ? [
    {year:plan.baseYear,...plan.baseLine},
    ...plan.plan,
  ] : [];

  const filteredSIG = plan ? SIG_ROWS.filter(r=>{
    if(r.isSep) return true;
    if(section==="all") return true;
    return r.section===section||r.key.startsWith("_");
  }) : [];

  return (
    <div>
      {/* Hypothèses */}
      <div style={card}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
          🔮 Hypothèses clés du Business Plan
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14,marginBottom:16}}>
          <div>
            <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Horizon (ans)</label>
            <select style={inputSt} value={hyp.years} onChange={e=>setHyp(h=>({...h,years:parseInt(e.target.value)}))}>
              {[3,4,5].map(y=><option key={y} value={y}>{y} ans</option>)}
            </select>
          </div>
          <div>
            <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Croissance masse salariale (%/an)</label>
            <input type="number" step="0.5" style={inputSt} value={hyp.payroll_growth}
              onChange={e=>setHyp(h=>({...h,payroll_growth:parseFloat(e.target.value)||0}))}/>
          </div>
          <div>
            <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Taux IS (%)</label>
            <input type="number" step="0.5" style={inputSt} value={hyp.is_rate}
              onChange={e=>setHyp(h=>({...h,is_rate:parseFloat(e.target.value)||25}))}/>
          </div>
          <div>
            <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Taux amort. moyen (%/an)</label>
            <input type="number" step="0.5" style={inputSt} value={hyp.amort_rate}
              onChange={e=>setHyp(h=>({...h,amort_rate:parseFloat(e.target.value)||15}))}/>
          </div>
          <div>
            <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Charges financières (€/an)</label>
            <input type="number" step="100" style={inputSt} value={hyp.financial_charges}
              onChange={e=>setHyp(h=>({...h,financial_charges:parseFloat(e.target.value)||0}))}/>
          </div>
          <div>
            <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>BFR — délai moyen (jours)</label>
            <input type="number" step="1" style={inputSt} value={hyp.bfr_days}
              onChange={e=>setHyp(h=>({...h,bfr_days:parseInt(e.target.value)||45}))}/>
          </div>
        </div>

        <div style={{marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--text-muted)",marginBottom:8}}>Taux de croissance du CA par année</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {Array.from({length:parseInt(hyp.years)}).map((_,i)=>(
              <div key={i} style={{minWidth:100}}>
                <label style={{display:"block",fontSize:11,color:"var(--text-muted)",marginBottom:3}}>N+{i+1} (%)</label>
                <input type="number" step="0.5" style={inputSt} value={hyp.growth_rates[i]||0}
                  onChange={e=>setGrowth(i,e.target.value)}/>
              </div>
            ))}
          </div>
        </div>

        <div style={{marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--text-muted)",marginBottom:8}}>Investissements prévus par année (€)</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {Array.from({length:parseInt(hyp.years)}).map((_,i)=>(
              <div key={i} style={{minWidth:120}}>
                <label style={{display:"block",fontSize:11,color:"var(--text-muted)",marginBottom:3}}>N+{i+1}</label>
                <input type="number" step="1000" style={inputSt} value={hyp.investments[i]||0}
                  onChange={e=>setInvest(i,e.target.value)}/>
              </div>
            ))}
          </div>
        </div>

        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <button onClick={generate} disabled={loading} style={{padding:"9px 20px",background:"#1a9e72",
            color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600}}>
            {loading?"Génération…":"📊 Générer le Business Plan"}
          </button>
          {plan && (
            <>
              <button onClick={exportPdf} disabled={exporting} style={{padding:"9px 20px",
                background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600}}>
                {exporting?"Export…":"⬇ PDF — Rapport banque/investisseurs"}
              </button>
              <ProtectedLink url={api.budget.exportBudgetUrl(scenarioId)}
                style={{padding:"9px 20px",background:"#f3f4f6",border:"1px solid #e5e7eb",
                  borderRadius:8,fontSize:13,fontWeight:600,textDecoration:"none",color:"var(--text,#1f2937)"}}>
                ⬇ Excel
              </ProtectedLink>
            </>
          )}
          {msg && <span style={{fontSize:12,color:"#dc2626"}}>{msg}</span>}
        </div>
      </div>

      {plan && (
        <>
          {/* KPI headline cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:20}}>
            {[
              {label:"CA base",        val:fmt(plan.baseLine.ca),           color:"#3b82f6"},
              {label:`CA N+${hyp.years}`,val:fmt(plan.plan[plan.plan.length-1]?.ca),color:"#3b82f6"},
              {label:`CAGR CA`,        val:plan.plan.length>0?fmtPct(((plan.plan[plan.plan.length-1]?.ca/plan.baseLine.ca)**(1/plan.plan.length)-1)*100):"—",color:"#8b5cf6"},
              {label:"EBE final",      val:fmt(plan.plan[plan.plan.length-1]?.ebe),  color:"#1a9e72"},
              {label:"RN final",       val:fmt(plan.plan[plan.plan.length-1]?.rn),   color:clr(plan.plan[plan.plan.length-1]?.rn)},
              {label:"CAF finale",     val:fmt(plan.plan[plan.plan.length-1]?.caf),  color:"#3b82f6"},
              {label:"FCF cumulé",     val:fmt(plan.plan.reduce((s,p)=>s+(p.fcf||0),0)),color:"#ec4899"},
              {label:"Marge EBE moy.",val:fmtPct(plan.plan.reduce((s,p)=>s+(p.ebeMargin||0),0)/Math.max(plan.plan.length,1)),color:"#1a9e72"},
            ].map(({label,val,color})=>(
              <div key={label} style={{...card,marginBottom:0,textAlign:"center",padding:"14px 12px"}}>
                <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>{label}</div>
                <div style={{fontSize:15,fontWeight:700,color}}>{val}</div>
              </div>
            ))}
          </div>

          {/* Multi-metric chart */}
          <div style={card}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:16}}>Évolution pluriannuelle</div>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={chartRows} margin={{top:4,right:10,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis dataKey="year" tick={{fontSize:11}}/>
                <YAxis yAxisId="left"  tick={{fontSize:10}} tickFormatter={fmtK}/>
                <YAxis yAxisId="right" orientation="right" tick={{fontSize:10}} tickFormatter={v=>`${v.toFixed(0)}%`}/>
                <Tooltip formatter={(v,n)=>typeof v==="number"&&Math.abs(v)>1?[fmt(v),n]:[fmtPct(v),n]}/>
                <Legend iconSize={10} wrapperStyle={{fontSize:11}}/>
                <Bar    yAxisId="left"  dataKey="ca"  name="Chiffre d'affaires" fill="#3b82f620" stroke="#3b82f6" strokeWidth={1}/>
                <Line   yAxisId="left"  type="monotone" dataKey="ebe"  name="EBE"          stroke="#1a9e72" strokeWidth={2.5} dot={{r:4}}/>
                <Line   yAxisId="left"  type="monotone" dataKey="rn"   name="Résultat net" stroke="#8b5cf6" strokeWidth={2} dot={{r:4}} strokeDasharray="5 3"/>
                <Line   yAxisId="left"  type="monotone" dataKey="caf"  name="CAF"          stroke="#3b82f6" strokeWidth={1.5} dot={{r:3}} strokeDasharray="3 2"/>
                <Line   yAxisId="right" type="monotone" dataKey="ebeMargin" name="Taux EBE %" stroke="#f59e0b" strokeWidth={1.5} dot={false} strokeDasharray="4 2"/>
                <ReferenceLine yAxisId="left" y={0} stroke="#e5e7eb"/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Free Cash-Flow waterfall chart */}
          <div style={card}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:16}}>Free Cash-Flow — Flux de trésorerie disponible</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartRows.slice(1)} margin={{top:4,right:10,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis dataKey="year" tick={{fontSize:11}}/>
                <YAxis tick={{fontSize:10}} tickFormatter={fmtK}/>
                <Tooltip formatter={(v,n)=>[fmt(v),n]}/>
                <Legend iconSize={10} wrapperStyle={{fontSize:11}}/>
                <Bar dataKey="caf"          name="CAF"            fill="#3b82f6" radius={[4,4,0,0]}/>
                <Bar dataKey="invest"       name="Investissements" fill="#ef444460" radius={[4,4,0,0]}/>
                <Bar dataKey="fcf"          name="Free Cash-Flow" fill="#ec4899" radius={[4,4,0,0]}/>
                <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 2"/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* SIG Table */}
          <div style={card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div>
                <div style={{fontSize:14,fontWeight:700}}>Soldes Intermédiaires de Gestion (SIG)</div>
                <div style={{fontSize:12,color:"var(--text-muted)"}}>Base {plan.baseYear} → {plan.baseYear+plan.plan.length}</div>
              </div>
              <div style={{display:"flex",gap:6}}>
                {[["all","Tout"],["top","VA / EBE"],["pl","P&L"],["cash","Cash"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setSection(v)} style={{
                    padding:"4px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",
                    border:`1px solid ${section===v?"#3b82f6":"#e5e7eb"}`,
                    background:section===v?"#3b82f6":"transparent",
                    color:section===v?"#fff":"var(--text-muted)",
                  }}>{l}</button>
                ))}
              </div>
            </div>

            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
                <thead>
                  <tr style={{background:"#f9fafb"}}>
                    <th style={{padding:"9px 12px",textAlign:"left",fontWeight:600,color:"#6b7280",
                      borderBottom:"1px solid #e5e7eb",minWidth:220}}>Indicateur</th>
                    <th style={{padding:"9px 12px",textAlign:"right",fontWeight:600,color:"#6b7280",
                      borderBottom:"1px solid #e5e7eb",whiteSpace:"nowrap"}}>Base {plan.baseYear}</th>
                    {plan.plan.map(p=>(
                      <th key={p.year} style={{padding:"9px 12px",textAlign:"right",fontWeight:600,
                        color:"#1a9e72",borderBottom:"1px solid #e5e7eb",whiteSpace:"nowrap"}}>
                        N+{p.year-plan.baseYear} ({p.year})
                        {p.growth!=null && <div style={{fontSize:10,color:"#6b7280",fontWeight:400}}>+{p.growth}%</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSIG.map(row=>{
                    if(row.isSep) return (
                      <tr key={row.key}>
                        <td colSpan={plan.plan.length+2} style={{
                          padding:"6px 12px",fontSize:11,fontWeight:700,color:"#6b7280",
                          background:"#f9fafb",borderTop:"2px solid #e5e7eb",
                          borderBottom:"1px solid #e5e7eb",
                        }}>{row.label}</td>
                      </tr>
                    );
                    const baseVal = plan.baseLine[row.key];
                    return (
                      <tr key={row.key} style={{
                        borderBottom:"1px solid #f3f4f6",
                        borderTop:row.separator?"2px solid #e5e7eb":"none",
                        background:row.bold?"#fafafa":"transparent",
                      }}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                        onMouseLeave={e=>e.currentTarget.style.background=row.bold?"#fafafa":""}>
                        <td style={{padding:"8px 12px",fontWeight:row.bold?700:400}}>{row.label}</td>
                        <td style={{padding:"8px 12px",textAlign:"right",color:row.color,fontWeight:row.bold?700:400}}>
                          {baseVal==null?"—":row.isPct?fmtPct(baseVal):fmt(baseVal)}
                        </td>
                        {plan.plan.map((p,pi)=>{
                          const v = p[row.key];
                          const prev = pi===0?plan.baseLine[row.key]:plan.plan[pi-1][row.key];
                          const pctChg = prev&&prev!==0?((v-prev)/Math.abs(prev)*100).toFixed(1):null;
                          return (
                            <td key={p.year} style={{padding:"8px 12px",textAlign:"right",
                              color:row.color,fontWeight:row.bold?700:400}}>
                              {v==null?"—":row.isPct?fmtPct(v):fmt(v)}
                              {!row.isPct&&pctChg&&(
                                <div style={{fontSize:9,color:Number(pctChg)>=0?"#16a34a":"#dc2626"}}>
                                  {Number(pctChg)>=0?"+":""}{pctChg}%
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* BFR forecast */}
          <div style={card}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Besoin en Fonds de Roulement (BFR)</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#f9fafb"}}>
                    <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"#6b7280",borderBottom:"1px solid #e5e7eb"}}>Indicateur</th>
                    {chartRows.map(p=>(
                      <th key={p.year} style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:"#6b7280",borderBottom:"1px solid #e5e7eb"}}>{p.year}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {label:"CA annuel",      key:"ca",           color:"#3b82f6"},
                    {label:"BFR (jours×CA/360)", key:"bfr",      color:"#f59e0b", bold:true, compute:(row)=>((row.ca||0)*hyp.bfr_days/360)},
                    {label:"Variation BFR",  key:"bfrVariation", color:"#f59e0b"},
                    {label:"CAF disponible", key:"caf",          color:"#1a9e72", bold:true},
                    {label:"Couverture BFR", key:"_cov",         color:"#6b7280",
                      compute:(row)=>row.caf&&row.bfrVariation!=null?row.caf-(row.bfrVariation||0):null},
                  ].map(({label,key,color,bold,compute})=>(
                    <tr key={key} style={{borderBottom:"1px solid #f3f4f6"}}>
                      <td style={{padding:"7px 12px",fontWeight:bold?700:400,fontSize:12}}>{label}</td>
                      {chartRows.map(p=>{
                        const bfr = (p.ca||0)*hyp.bfr_days/360;
                        const rowData = {...p,bfr};
                        const v = compute ? compute(rowData) : p[key];
                        return (
                          <td key={p.year} style={{padding:"7px 12px",textAlign:"right",
                            fontWeight:bold?700:400,color:v!=null&&v<0?"#dc2626":color,fontSize:12}}>
                            {v==null?"—":fmt(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Plan de financement */}
          <div style={card}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Plan de financement prévisionnel</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#f9fafb"}}>
                    {["Emplois / Ressources","Base"].concat(plan.plan.map(p=>`N+${p.year-plan.baseYear}`))
                      .map((h,i)=>(
                        <th key={i} style={{padding:"8px 12px",textAlign:i===0?"left":"right",
                          fontWeight:600,color:"#6b7280",borderBottom:"1px solid #e5e7eb"}}>{h}</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {label:"RESSOURCES",       isTitle:true},
                    {label:"CAF",              key:"caf",    color:"#1a9e72"},
                    {label:"EMPLOIS",          isTitle:true},
                    {label:"Investissements",  key:"invest", color:"#ef4444"},
                    {label:"Variation BFR",    key:"bfrVariation",color:"#f59e0b"},
                    {label:"SOLDE (FCF)",      key:"fcf",    color:"#3b82f6",bold:true,separator:true},
                  ].map(({label,key,color,bold,isTitle,separator})=>{
                    if(isTitle) return (
                      <tr key={label}><td colSpan={plan.plan.length+2} style={{
                        padding:"6px 12px",fontSize:11,fontWeight:700,
                        color:"#6b7280",background:"#f9fafb",
                        borderTop:"1px solid #e5e7eb",borderBottom:"1px solid #e5e7eb",
                      }}>{label}</td></tr>
                    );
                    return (
                      <tr key={label} style={{
                        borderBottom:"1px solid #f3f4f6",
                        borderTop:separator?"2px solid #e5e7eb":"none",
                        background:bold?"#fafafa":"transparent",
                      }}>
                        <td style={{padding:"7px 12px",fontWeight:bold?700:400}}>{label}</td>
                        <td style={{padding:"7px 12px",textAlign:"right",color,fontWeight:bold?700:400}}>
                          {plan.baseLine[key]==null?"—":fmt(plan.baseLine[key])}
                        </td>
                        {plan.plan.map(p=>(
                          <td key={p.year} style={{padding:"7px 12px",textAlign:"right",
                            color:p[key]<0?"#dc2626":color,fontWeight:bold?700:400}}>
                            {p[key]==null?"—":fmt(p[key])}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{marginTop:14,fontSize:12,color:"var(--text-muted)",lineHeight:1.6}}>
              <strong>Note :</strong> Le solde positif (FCF &gt; 0) indique une capacité de remboursement de dettes ou de distribution.
              Un solde négatif nécessite un recours à l'endettement ou aux fonds propres.
              BFR calculé sur {hyp.bfr_days} jours de CA.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
